// =============================================================================
// conversations/components/StartConversationModal.tsx — Escolha da caixa ao
// iniciar conversa (F43-S11, docs/planejamento-inbox-vendedores-waha.md §3.6).
//
// Componente ÚNICO usado nos dois pontos de entrada (DoD do slot):
//   - CRM: ficha do lead → botão "Iniciar conversa" (CrmDetailPage.tsx)
//   - Live chat: ChatList → botão "Nova conversa" (via NewConversationDialog)
//
// Fluxo:
//   1. GET canais-disponiveis → lista de caixas, cada uma com o que ela
//      exige HOJE, em português (startConversationChoice.ts traduz).
//   2. Caixa indisponível aparece desabilitada COM motivo — nunca some.
//   3. Escolher uma caixa que exige template → abre o seletor de template
//      (catálogo aprovado da organização); caixa livre → campo de texto.
//   4. Confirmar → POST iniciar (useStartConversationOnChannel).
//
// Nenhuma regra de janela é reimplementada aqui — só o que o backend já
// decidiu (channel-picker.schema.ts), traduzido pela função pura.
//
// DS: elev-5 (diálogo), Spotlight hover nos cards de caixa, tokens canônicos.
// =============================================================================

import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../../../components/ui/Button';
import { ApiError } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { useFocusTrap } from '../../sales/components/useFocusTrap';
import { useApprovedTemplatesForStart } from '../hooks/useApprovedTemplatesForStart';
import { useAvailableChannels } from '../hooks/useAvailableChannels';
import type {
  StartConversationOnChannelError,
  StartConversationOnChannelResponse,
} from '../hooks/useStartConversationOnChannel';
import { useStartConversationOnChannel } from '../hooks/useStartConversationOnChannel';
import type { ChannelChoiceDisplay } from '../startConversationChoice';
import { describeChannelChoices, firstAvailableChoice } from '../startConversationChoice';

// ─── Placeholders {{N}} — mesma lógica de MessageComposer/TemplateSelector ────

function extractPlaceholders(bodyText: string): string[] {
  const matches = bodyText.matchAll(/\{\{(\d+)\}\}/g);
  const seen = new Set<string>();
  const positions: string[] = [];
  for (const match of matches) {
    const pos = match[1];
    if (pos !== undefined && !seen.has(pos)) {
      seen.add(pos);
      positions.push(pos);
    }
  }
  return positions;
}

function interpolate(bodyText: string, values: Record<string, string>): string {
  return bodyText.replace(/\{\{(\d+)\}\}/g, (_match, pos: string) => values[pos] ?? `{{${pos}}}`);
}

// ─── Props ──────────────────────────────────────────────────────────────────

export interface StartConversationModalProps {
  leadId: string;
  leadName: string;
  onClose: () => void;
  onStarted: (result: StartConversationOnChannelResponse) => void;
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function ChannelListSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2" aria-hidden="true">
      {[1, 2].map((n) => (
        <div
          key={n}
          className="rounded-md border border-border bg-surface-hover animate-pulse h-16"
        />
      ))}
    </div>
  );
}

// ─── Card de caixa (rádio custom, Spotlight hover) ───────────────────────────

interface ChannelCardProps {
  choice: ChannelChoiceDisplay;
  selected: boolean;
  onSelect: () => void;
}

function ChannelCard({ choice, selected, onSelect }: ChannelCardProps): React.JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={choice.disabled}
      onClick={onSelect}
      className={cn(
        'w-full text-left rounded-md border px-4 py-3 transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
        choice.disabled
          ? 'border-border-subtle bg-surface-2 opacity-60 cursor-not-allowed'
          : selected
            ? [
                'border-azul/40 bg-azul/5',
                '[box-shadow:var(--elev-1),inset_0_1px_0_rgba(27,58,140,0.08)]',
              ]
            : [
                'border-border bg-surface-1',
                '[box-shadow:var(--elev-1),inset_0_1px_0_rgba(255,255,255,0.06)]',
                'hover:border-border-subtle hover:bg-surface-hover',
                'hover:[box-shadow:var(--elev-2)]',
                'active:[box-shadow:var(--elev-0)]',
              ],
      )}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className={cn(
            'mt-0.5 w-4 h-4 rounded-full border shrink-0 flex items-center justify-center',
            choice.disabled ? 'border-ink-4' : selected ? 'border-azul' : 'border-border-strong',
          )}
        >
          {selected && !choice.disabled && (
            <span className="w-2 h-2 rounded-full bg-azul" aria-hidden="true" />
          )}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-sans text-sm font-semibold text-ink truncate">{choice.label}</p>
          <p
            className={cn(
              'font-sans text-xs mt-0.5 leading-relaxed',
              choice.disabled ? 'text-danger' : 'text-ink-3',
            )}
          >
            {choice.description}
          </p>
        </div>
      </div>
    </button>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function StartConversationModal({
  leadId,
  leadName,
  onClose,
  onStarted,
}: StartConversationModalProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);

  const { data: channels, isLoading, isError, refetch } = useAvailableChannels(leadId);
  const choices = React.useMemo(() => describeChannelChoices(channels ?? []), [channels]);

  const [selectedChannelId, setSelectedChannelId] = React.useState<string | null>(null);
  const [corpo, setCorpo] = React.useState('');
  const [templateId, setTemplateId] = React.useState('');
  const [variableValues, setVariableValues] = React.useState<Record<string, string>>({});
  const [submitError, setSubmitError] = React.useState<StartConversationOnChannelError | null>(
    null,
  );

  // Pré-seleciona a primeira caixa disponível assim que a lista chega —
  // vendedor com uma única caixa não precisa clicar em nada (nota do slot:
  // "não deve virar fricção inútil").
  React.useEffect(() => {
    if (selectedChannelId === null && choices.length > 0) {
      const first = firstAvailableChoice(choices);
      if (first) setSelectedChannelId(first.channelId);
    }
  }, [choices, selectedChannelId]);

  const selectedChoice = choices.find((c) => c.channelId === selectedChannelId) ?? null;

  // F43-S48: falha ao carregar o catálogo (403, 500, rede) NUNCA trava a
  // tela inteira — só a caixa que exige template fica indisponível, com
  // motivo legível. O canal de texto livre (ex.: WhatsApp pessoal/WAHA)
  // continua utilizável, porque nem consulta este hook (`enabled` acima).
  const {
    data: approvedTemplates,
    isLoading: templatesLoading,
    isError: templatesError,
    error: templatesErrorObj,
    refetch: refetchTemplates,
  } = useApprovedTemplatesForStart({
    enabled: selectedChoice?.requiresTemplate === true,
  });
  const templatesErrorMessage =
    templatesErrorObj instanceof ApiError && templatesErrorObj.status === 403
      ? 'Você não tem permissão para ver as mensagens aprovadas desta organização. ' +
        'Fale com quem administra o sistema para liberar o acesso — ou escolha uma caixa ' +
        'que não exija mensagem aprovada.'
      : 'Não foi possível carregar as mensagens aprovadas agora.';
  const selectedTemplate = approvedTemplates?.find((t) => t.id === templateId) ?? null;
  const placeholders = selectedTemplate ? extractPlaceholders(selectedTemplate.body) : [];
  const allVariablesFilled = placeholders.every(
    (pos) => (variableValues[pos] ?? '').trim().length > 0,
  );

  // Reset de rascunho ao trocar de caixa — nada do texto/template de uma
  // caixa deve vazar para outra.
  function handleSelectChannel(channelId: string): void {
    setSelectedChannelId(channelId);
    setCorpo('');
    setTemplateId('');
    setVariableValues({});
    setSubmitError(null);
  }

  function handleSelectTemplate(id: string): void {
    setTemplateId(id);
    setVariableValues({});
  }

  const { startConversation, isPending } = useStartConversationOnChannel({
    onSuccess: (response) => {
      setSubmitError(null);
      onStarted(response);
    },
    onError: (error) => setSubmitError(error),
  });

  const isPendingRef = React.useRef(isPending);
  isPendingRef.current = isPending;
  const handleEscape = React.useCallback(() => {
    if (!isPendingRef.current) onClose();
  }, [onClose]);
  useFocusTrap({ containerRef: panelRef, active: true, onEscape: handleEscape });

  const canSubmit =
    selectedChoice !== null &&
    !selectedChoice.disabled &&
    !isPending &&
    (selectedChoice.requiresTemplate
      ? selectedTemplate !== null && allVariablesFilled
      : corpo.trim().length > 0);

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit || !selectedChoice) return;

    if (selectedChoice.requiresTemplate && selectedTemplate) {
      startConversation({
        leadId,
        channelId: selectedChoice.channelId,
        templateName: selectedTemplate.name,
        // Sem languageCode: o backend resolve o idioma cadastrado do
        // template pelo nome (start-channel.schema.ts) — nunca duplicar o
        // dado aqui.
        variables: placeholders.map((pos) => (variableValues[pos] ?? '').trim()),
      });
      return;
    }

    startConversation({
      leadId,
      channelId: selectedChoice.channelId,
      corpo: corpo.trim(),
    });
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleEscape();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-conversation-modal-title"
        className="w-full max-w-lg rounded-md flex flex-col overflow-hidden max-h-[min(90vh,780px)]"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-6 py-5 shrink-0"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="flex flex-col gap-1">
            <h2
              id="start-conversation-modal-title"
              className="font-display font-bold text-ink"
              style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
            >
              Falar com {leadName}
            </h2>
            <p className="font-sans text-sm text-ink-3">Escolha por qual número falar</p>
          </div>
          <button
            type="button"
            onClick={handleEscape}
            disabled={isPending}
            aria-label="Fechar"
            className={cn(
              'p-2 rounded-sm text-ink-3 hover:text-ink hover:bg-surface-hover',
              'transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
            )}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M3 3l10 10M13 3L3 13" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-5 px-6 py-5 overflow-y-auto">
          {/* Estado: carregando caixas */}
          {isLoading && <ChannelListSkeleton />}

          {/* Estado: erro */}
          {isError && !isLoading && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{ borderColor: 'var(--danger)', background: 'var(--danger-bg)' }}
            >
              <p className="font-sans font-semibold text-sm text-danger">
                Não foi possível carregar as caixas disponíveis
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => refetch()}
              >
                Tentar novamente
              </Button>
            </div>
          )}

          {/* Estado: vazio */}
          {!isLoading && !isError && channels !== undefined && choices.length === 0 && (
            <p className="font-sans text-sm text-ink-3">
              Nenhuma caixa de WhatsApp disponível para você falar com este contato.
            </p>
          )}

          {/* Lista de caixas */}
          {!isLoading && !isError && choices.length > 0 && (
            <div role="radiogroup" aria-label="Caixa de WhatsApp" className="flex flex-col gap-2">
              {choices.map((choice) => (
                <ChannelCard
                  key={choice.channelId}
                  choice={choice}
                  selected={selectedChannelId === choice.channelId}
                  onSelect={() => handleSelectChannel(choice.channelId)}
                />
              ))}
            </div>
          )}

          {/* Corpo dependente da caixa escolhida */}
          {selectedChoice && !selectedChoice.disabled && (
            <>
              {selectedChoice.requiresTemplate ? (
                <div className="flex flex-col gap-3">
                  {templatesLoading ? (
                    <div
                      className="h-24 rounded-sm animate-pulse"
                      style={{ background: 'var(--surface-muted)' }}
                    />
                  ) : templatesError ? (
                    // F43-S48: 403/erro no catálogo NUNCA fica silencioso nem
                    // trava a tela — só esta caixa (que exige template) fica
                    // indisponível, com motivo explícito e ação de retry.
                    // Canais de texto livre continuam funcionando (ver DoD).
                    <div
                      role="alert"
                      className="rounded-sm px-4 py-3 border-l-[3px]"
                      style={{ borderColor: 'var(--danger)', background: 'var(--danger-bg)' }}
                    >
                      <p className="font-sans font-semibold text-sm text-danger">
                        Não foi possível carregar as mensagens aprovadas
                      </p>
                      <p className="font-sans text-xs text-ink-2 mt-0.5">{templatesErrorMessage}</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => refetchTemplates()}
                      >
                        Tentar novamente
                      </Button>
                    </div>
                  ) : approvedTemplates && approvedTemplates.length > 0 ? (
                    <>
                      <label
                        htmlFor="start-modal-template"
                        className="font-sans text-xs font-bold uppercase tracking-[0.08em] text-ink-3"
                      >
                        Mensagem aprovada
                      </label>
                      <select
                        id="start-modal-template"
                        value={templateId}
                        onChange={(e) => handleSelectTemplate(e.target.value)}
                        className={cn(
                          'w-full font-sans text-sm text-ink',
                          'bg-surface-1 rounded-sm px-[14px] py-[10px]',
                          'border border-border-strong',
                          'shadow-[inset_0_1px_2px_var(--border-inner-dark)]',
                          'focus:outline-none focus:border-azul',
                        )}
                      >
                        <option value="">Selecione…</option>
                        {approvedTemplates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>

                      {selectedTemplate &&
                        placeholders.map((pos) => {
                          const varLabel =
                            selectedTemplate.variables[Number(pos) - 1] ?? `Variável ${pos}`;
                          const inputId = `start-modal-var-${pos}`;
                          return (
                            <div key={pos} className="flex flex-col gap-1">
                              <label
                                htmlFor={inputId}
                                className="font-sans text-xs font-medium text-ink-2"
                              >
                                {varLabel}{' '}
                                <span className="font-mono text-ink-4 font-normal">{`{{${pos}}}`}</span>
                              </label>
                              <input
                                id={inputId}
                                type="text"
                                value={variableValues[pos] ?? ''}
                                onChange={(e) =>
                                  setVariableValues((prev) => ({
                                    ...prev,
                                    [pos]: e.target.value,
                                  }))
                                }
                                className={cn(
                                  'w-full rounded-xs px-3 py-1.5 font-sans text-sm text-ink',
                                  'bg-surface-inset border border-border',
                                  'focus:outline-none focus:border-azul',
                                )}
                              />
                            </div>
                          );
                        })}

                      {selectedTemplate && (
                        <div
                          className="rounded-xs border border-border px-3 py-2 bg-surface-inset"
                          style={{ boxShadow: 'inset 0 1px 3px rgba(20,33,61,0.06)' }}
                        >
                          <p className="font-sans text-xs text-ink-3 leading-relaxed">
                            {interpolate(selectedTemplate.body, variableValues)}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="font-sans text-sm text-ink-3">
                      Nenhuma mensagem aprovada disponível ainda. Fale com quem cuida das aprovações
                      antes de iniciar por esta caixa.
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="start-modal-corpo"
                    className="font-sans text-xs font-bold uppercase tracking-[0.08em] text-ink-3"
                  >
                    Mensagem
                  </label>
                  <textarea
                    id="start-modal-corpo"
                    rows={4}
                    value={corpo}
                    onChange={(e) => setCorpo(e.target.value)}
                    placeholder="Escreva a primeira mensagem…"
                    maxLength={4096}
                    className={cn(
                      'w-full font-sans text-sm text-ink resize-none',
                      'bg-surface-1 rounded-sm px-[14px] py-[11px]',
                      'border border-border-strong',
                      'shadow-[inset_0_1px_2px_var(--border-inner-dark)]',
                      'placeholder:text-ink-4',
                      'focus:outline-none focus:border-azul',
                    )}
                  />
                </div>
              )}
            </>
          )}

          {submitError && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{
                borderColor: 'var(--danger)',
                background: 'var(--danger-bg)',
                boxShadow: 'var(--elev-1)',
              }}
            >
              <p className="font-sans font-semibold text-sm text-danger">
                Não foi possível iniciar a conversa
              </p>
              <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError.message}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex flex-col gap-2.5 px-6 py-5 shrink-0"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <form onSubmit={handleSubmit}>
            <Button
              type="submit"
              variant="primary"
              disabled={!canSubmit}
              className="w-full justify-center"
            >
              {isPending ? 'Enviando…' : 'Iniciar conversa'}
            </Button>
          </form>
          <Button
            type="button"
            variant="ghost"
            onClick={handleEscape}
            disabled={isPending}
            className="w-full justify-center"
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
